import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Loader from "../../components/Loader";
import SalesOverview from "../../components/SalesOverview";
import TasksOverview from "../../components/TasksOverview";
import TodosOverview from "../../components/TodosOverview";
import TodaySchedule from "../../components/TodaySchedule";
import { displayToast } from "../../utils/Toast";
import ProfilePlaceholder from "../../assets/svgIcons/ProfilePlaceholder.svg";
import EditWhite from "../../assets/svgIcons/EditWhite.svg";
import { fetchProfile } from "../../apis/profile";
import { useErrorHandler } from "../../services/ErrorHandler";
import "../../styles/pages/home/Home.css";
import { fetchTransactionByCode } from "../../apis/transaction";
import useAutoRefresh from "../../hooks/useAutoRefresh";

const DEFAULT_DASHBOARD_SETTINGS = {
  employeesStrength: "",
  employeesSubLabel: "",
  productionCapacity: "",
  productionCapacitySubLabel: "",
  isoCertificationStatus: "",
  isoCertificationSubLabel: "",
  openWorkOrders: "",
  openWorkOrdersSubLabel: "",
  totalProduction: "",
  totalProductionSubLabel: "",
};

const mapDashboardSettings = (orgData) => ({
  employeesStrength:
    orgData?.employees ?? DEFAULT_DASHBOARD_SETTINGS.employeesStrength,
  employeesSubLabel:
    orgData?.employees_subtitle ?? DEFAULT_DASHBOARD_SETTINGS.employeesSubLabel,
  productionCapacity:
    orgData?.production_capacity ??
    DEFAULT_DASHBOARD_SETTINGS.productionCapacity,
  productionCapacitySubLabel:
    orgData?.production_capacity_subtitle ??
    DEFAULT_DASHBOARD_SETTINGS.productionCapacitySubLabel,
  isoCertificationStatus:
    orgData?.iso_certification ??
    DEFAULT_DASHBOARD_SETTINGS.isoCertificationStatus,
  isoCertificationSubLabel:
    orgData?.iso_subtitle ??
    DEFAULT_DASHBOARD_SETTINGS.isoCertificationSubLabel,
  openWorkOrders: DEFAULT_DASHBOARD_SETTINGS.openWorkOrders,
  openWorkOrdersSubLabel: DEFAULT_DASHBOARD_SETTINGS.openWorkOrdersSubLabel,
  totalProduction:
    orgData?.total_production ?? DEFAULT_DASHBOARD_SETTINGS.totalProduction,
  totalProductionSubLabel:
    orgData?.total_production_subtitle ??
    DEFAULT_DASHBOARD_SETTINGS.totalProductionSubLabel,
});

const fetchOpenWorkOrdersCount = async () => {
  try {
    const response = await fetchTransactionByCode({
      transactionCode: "WO",
      page: 1,
      pageSize: 1,
    });

    const totalCount = Number(
      response?.TotalCount ??
        response?.total_count ??
        response?.totalCount ??
        response?.count,
    );

    if (Number.isFinite(totalCount) && totalCount >= 0) {
      return String(totalCount);
    }

    if (Array.isArray(response?.TransactionList)) {
      return String(response.TransactionList.length);
    }

    return "";
  } catch (error) {
    return "";
  }
};

const Home = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [organisation, setOrganisation] = useState(null);
  const [dashboardSettings, setDashboardSettings] = useState(
    DEFAULT_DASHBOARD_SETTINGS,
  );
  const { handleErrorResponse } = useErrorHandler();

  const fetchOrganizationData = useCallback(
    async ({ showLoader = false } = {}) => {
      if (showLoader) {
        setIsLoading(true);
      }

      try {
        const [response, openWorkOrders] = await Promise.all([
          fetchProfile(),
          fetchOpenWorkOrdersCount(),
        ]);

        if (response.description) {
          handleErrorResponse(response.description);
          if (showLoader) {
            displayToast(response.description, "error");
          }
          return;
        }

        setOrganisation(response);
        localStorage.setItem("organisation", JSON.stringify(response));
        setDashboardSettings({
          ...mapDashboardSettings(response),
          openWorkOrders,
        });
      } catch (error) {
        handleErrorResponse(error);
        if (showLoader) {
          displayToast("Failed to load profile data", "error");
        }
      } finally {
        if (showLoader) {
          setIsLoading(false);
        }
      }
    },
    [handleErrorResponse],
  );
  useEffect(() => {
    fetchOrganizationData({ showLoader: true });
  }, [fetchOrganizationData]);

  useAutoRefresh(() => fetchOrganizationData());

  const formatAddress = (org) => {
    if (!org) return "";
    const addressParts = [
      org.address,
      org.city,
      org.district,
      org.state,
      org.country,
      org.pincode,
    ].filter((part) => part && part.trim() !== "");

    return addressParts.join(", ");
  };

  const formatShortAddress = (org) => {
    if (!org) return "";

    const compact = (value) => (value || "").replace(/\s+/g, " ").trim();
    const normalized = (value) =>
      compact(value)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();

    const splitAddress = String(org.address || "")
      .split(",")
      .map((segment) => compact(segment))
      .filter(Boolean);

    const structuredParts = [org.city, org.district, org.state, org.country]
      .map((part) => compact(part))
      .filter(Boolean);

    const seen = new Set();
    const uniqueSegments = [...splitAddress, ...structuredParts].filter(
      (item) => {
        const key = normalized(item);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      },
    );

    const citySegment = structuredParts[0] || "";
    const cityKey = normalized(citySegment);

    const cleanLocalitySegment = (value) =>
      compact(value)
        .replace(/\bphase\s*[-]?\s*(\d+|[ivx]+)\b/gi, "")
        .replace(/\s{2,}/g, " ")
        .replace(/[,-]+$/g, "")
        .trim();

    const cleanCitySegment = (value) =>
      compact(value)
        .replace(/\s{2,}/g, " ")
        .replace(/[,-]+$/g, "")
        .trim();

    const localityPriorityPattern =
      /\b(phase|sector|area|layout|nagar|industrial|estate|park|extension|city|locality)\b/i;
    const housePattern = /\b(shed|unit|flat|door|plot|no\.?|number)\b/i;

    const localityCandidates = splitAddress
      .map((segment) => {
        const key = normalized(segment);
        if (!key || key === cityKey) return null;
        if (/^[\d\s\-\/]+$/.test(segment)) return null;
        if (/^\d{5,6}$/.test(segment.replace(/\D/g, ""))) return null;
        if (!/[a-z]/i.test(segment)) return null;

        const priority = localityPriorityPattern.test(segment) ? 2 : 0;
        const penalty = housePattern.test(segment) ? 2 : 0;
        const score = priority - penalty;
        return { segment, score };
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score);

    const localitySegment = cleanLocalitySegment(
      localityCandidates[0]?.segment || "",
    );
    const cleanedCitySegment = cleanCitySegment(citySegment);

    const shortParts = [localitySegment, cleanedCitySegment].filter(Boolean);
    if (shortParts.length) {
      return shortParts.join(", ");
    }

    if (uniqueSegments.length >= 2) {
      return `${uniqueSegments[0]}, ${uniqueSegments[1]}`;
    }

    return uniqueSegments[0] || "";
  };

  const handleOpenEdit = () => {
    navigate("/profile", {
      state: { editMode: true, editDashboard: true, fromHomeEdit: true },
    });
  };

  const getStatIcon = (type) => {
    switch (type) {
      case "employees":
        return (
          <svg viewBox="0 0 80 80" aria-hidden="true">
            <defs>
              <linearGradient id="employeesGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#3f8cff" />
                <stop offset="100%" stopColor="#1f61e0" />
              </linearGradient>
            </defs>
            <circle cx="30" cy="24" r="12" fill="url(#employeesGrad)" />
            <path
              d="M8 56c0-10 10-17 22-17s22 7 22 17v6H8z"
              fill="url(#employeesGrad)"
            />
            <circle
              cx="56"
              cy="28"
              r="9"
              fill="url(#employeesGrad)"
              opacity="0.95"
            />
            <path
              d="M46 61c0-7 7-12 15-12s15 5 15 12v1H46z"
              fill="url(#employeesGrad)"
              opacity="0.95"
            />
          </svg>
        );
      case "production":
        return (
          <svg viewBox="0 0 80 80" aria-hidden="true">
            <rect x="8" y="57" width="64" height="5" rx="2" fill="#49a957" />
            <path
              d="M10 57V34l18-11v34zm20 0V29h10v28zm14 0V36l18-10v31z"
              fill="#49a957"
            />
            <path d="M22 23h7l2-8h-7zM42 29h7l2-9h-7z" fill="#49a957" />
            <path
              d="M25 12c5-4 11 0 14-2"
              fill="none"
              stroke="#49a957"
              strokeWidth="3"
              strokeLinecap="round"
            />
            <rect x="16" y="44" width="8" height="4" rx="1" fill="#f4fff6" />
            <rect x="34" y="44" width="8" height="4" rx="1" fill="#f4fff6" />
            <rect x="52" y="44" width="8" height="4" rx="1" fill="#f4fff6" />
          </svg>
        );
      case "iso":
        return (
          <svg viewBox="0 0 80 80" aria-hidden="true">
            <defs>
              <linearGradient id="isoGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#4e81e8" />
                <stop offset="100%" stopColor="#8b59f0" />
              </linearGradient>
            </defs>
            <path
              d="M40 8c11 8 23 10 30 10v17c0 17-12 28-30 37C22 63 10 52 10 35V18c7 0 19-2 30-10z"
              fill="none"
              stroke="url(#isoGrad)"
              strokeWidth="5"
            />
            <path
              d="M24 39l11 12 21-23"
              fill="none"
              stroke="url(#isoGrad)"
              strokeWidth="6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case "orders":
        return (
          <svg viewBox="0 0 80 80" aria-hidden="true">
            <rect
              x="17"
              y="11"
              width="46"
              height="57"
              rx="8"
              fill="none"
              stroke="#ff6b10"
              strokeWidth="5"
            />
            <rect
              x="29"
              y="7"
              width="22"
              height="10"
              rx="4"
              fill="#fff7f1"
              stroke="#ff6b10"
              strokeWidth="4"
            />
            <rect x="27" y="30" width="26" height="4" rx="2" fill="#ff6b10" />
            <rect x="27" y="41" width="26" height="4" rx="2" fill="#ff6b10" />
            <rect x="27" y="52" width="26" height="4" rx="2" fill="#ff6b10" />
          </svg>
        );
      case "total":
        return (
          <svg viewBox="0 0 80 80" aria-hidden="true">
            <defs>
              <radialGradient id="totalGrad" cx="0.5" cy="0.5" r="0.6">
                <stop offset="0%" stopColor="#f5fbe7" />
                <stop offset="100%" stopColor="#e8f5d6" />
              </radialGradient>
            </defs>
            <circle cx="40" cy="40" r="36" fill="url(#totalGrad)" />
            <rect x="20" y="45" width="8" height="12" rx="2" fill="#0a7f46" />
            <rect x="33" y="38" width="8" height="19" rx="2" fill="#0a7f46" />
            <rect x="46" y="30" width="8" height="27" rx="2" fill="#0a7f46" />
            <path
              d="M20 33l13-10 9 7 14-14"
              fill="none"
              stroke="#0a7f46"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M58 16v7h-7"
              fill="none"
              stroke="#0a7f46"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      default:
        return null;
    }
  };

  const statCards = [
    {
      title: "Employees",
      value: dashboardSettings.employeesStrength,
      subtitle: dashboardSettings.employeesSubLabel,
      type: "employees",
      icon: getStatIcon("employees"),
    },
    {
      title: "Production Capacity",
      value: dashboardSettings.productionCapacity,
      subtitle: dashboardSettings.productionCapacitySubLabel,
      type: "production",
      icon: getStatIcon("production"),
    },
    {
      title: "ISO Certification",
      value: dashboardSettings.isoCertificationStatus,
      subtitle: dashboardSettings.isoCertificationSubLabel,
      type: "iso",
      icon: getStatIcon("iso"),
    },
    {
      title: "Open Work Orders",
      value: dashboardSettings.openWorkOrders,
      subtitle: dashboardSettings.openWorkOrdersSubLabel,
      type: "orders",
      icon: getStatIcon("orders"),
    },
    {
      title: "Total Production",
      value: dashboardSettings.totalProduction,
      subtitle: dashboardSettings.totalProductionSubLabel,
      type: "total",
      icon: getStatIcon("total"),
    },
  ];

  const locationText =
    formatShortAddress(organisation) || formatAddress(organisation);

  return isLoading ? (
    <Loader />
  ) : (
    <div className="home home-v2">
      <section className="home-company-block">
        <div className="home-company-card">
          <img
            src={organisation?.logo_url || ProfilePlaceholder}
            alt="Company Logo"
            className="company-logo"
            onError={(event) => {
              event.target.src = ProfilePlaceholder;
            }}
          />
          <div className="company-info">
            <p className="company-greeting">Welcome,</p>
            <h2 className="home-company-name">
              {organisation?.display_name || organisation?.name || ""}
            </h2>
            <p className="company-subtitle">
              {organisation?.designation || ""}
            </p>
            <p className="company-address">
              {locationText && (
                <>
                  <span className="company-address-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 22s7-6.4 7-12a7 7 0 1 0-14 0c0 5.6 7 12 7 12z"
                        fill="#4b7bc8"
                        opacity="0.2"
                      />
                      <path
                        d="M12 22s7-6.4 7-12a7 7 0 1 0-14 0c0 5.6 7 12 7 12z"
                        stroke="#4b7bc8"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle cx="12" cy="10" r="2.5" fill="#4b7bc8" />
                    </svg>
                  </span>
                  <span>{locationText}</span>
                </>
              )}
            </p>
          </div>
        </div>
        <button className="edit-button" onClick={handleOpenEdit}>
          <img src={EditWhite} alt="Edit" className="edit-icon" />
          Edit
        </button>
      </section>

      <section className="home-stat-grid" aria-label="Dashboard Highlights">
        {statCards.map((card) => (
          <article className={`home-stat-card ${card.type}`} key={card.title}>
            <div className="stat-main">
              <div className="stat-icon">{card.icon}</div>
              <div className="stat-copy">
                <p className="stat-title">{card.title}</p>
                <p className="stat-value">{card.value || "-"}</p>
                <p className="stat-subtitle">{card.subtitle ?? ""}</p>
              </div>
            </div>
          </article>
        ))}
      </section>

      <SalesOverview />

      <div className="home-personal-row">
        <TodosOverview />
        <TodaySchedule />
      </div>

      <TasksOverview />
    </div>
  );
};

export default Home;
